import React, { useState } from "react";

// ═══════════════════════════════════════════════════════════════════════
// RESONANCE CREATIVE SUITE — Launcher Dashboard
// Routes to Production, Studio, Canvas, Illustrator, Publisher, Darkroom
// Main Street Group LLC
// ═══════════════════════════════════════════════════════════════════════

const T = {
  bg: "#0A0A0A", surface: "#111111", panel: "#0D0D0D",
  border: "#1E1E1E", text: "#C8C8C8", textDim: "#888888", textMuted: "#555555",
  gold: "#D4AF37", goldDim: "#8B7023", goldGlow: "rgba(212,175,55,0.15)",
};

const APPS = [
  {
    id: "production", name: "Production", subtitle: "VIDEO + MIX STUDIO",
    icon: "🎬", color: "#5FA8E8",
    desc: "Bring in a performance video, mix it, cut it, title it, and export it back in sync.",
    features: ["HEVC/.mov ingest (native or ffmpeg.wasm)", "Auto demux to editable tracks", "EQ · compression · delay · reverb · master bus", "Trim, fades, lower-third titles", "Shared LUT looks", "Re-synced 1080p MP4 + mix & stems WAV"],
    tier: "NEW · BUILD 002",
  },
  {
    id: "studio", name: "Studio", subtitle: "DIGITAL AUDIO WORKSTATION",
    icon: "🎵", color: "#D4AF37",
    desc: "Multi-track recording, MIDI, virtual instruments, mastering, and AI mix analysis.",
    features: ["Multi-track recording", "MIDI engine & piano roll", "Virtual instruments & drum machine", "Mastering chain (EQ, compressor, limiter)", "LUFS metering", "AI mix analysis"],
    tier: "T3 · 1,368 lines",
  },
  {
    id: "canvas", name: "Canvas", subtitle: "RASTER IMAGE EDITOR",
    icon: "🎨", color: "#E74C3C",
    desc: "Layers, brushes, filters, selections, masks, curves, and AI enhancement.",
    features: ["16 tools (brush, eraser, clone, smudge...)", "Layer masks & blend modes", "Curves, levels, color balance", "Layer FX (shadow, glow, stroke)", "Histogram & AI color analyzer", "Magic wand & flood fill"],
    tier: "T3 · 1,124 lines",
  },
  {
    id: "illustrator", name: "Illustrator", subtitle: "VECTOR GRAPHICS EDITOR",
    icon: "✏️", color: "#3498DB",
    desc: "Pen tool, shapes, paths, text, alignment, and SVG export.",
    features: ["Pen tool with bezier curves", "Shapes (rect, ellipse, polygon, star)", "Text with letter spacing", "SVG & PNG export", "Alignment tools", "Drag-and-drop object reorder"],
    tier: "PRO · 382 lines",
  },
  {
    id: "publisher", name: "Publisher", subtitle: "PAGE LAYOUT EDITOR",
    icon: "📄", color: "#2ECC71",
    desc: "Multi-page documents, text frames, image frames, paragraph styles, and export.",
    features: ["Multi-page documents", "Text frames with columns", "Image frames with fit modes", "8 paragraph styles", "Margins, guides, grid", "Master page framework"],
    tier: "PRO · 394 lines",
  },
  {
    id: "darkroom", name: "Darkroom", subtitle: "PHOTO EDITOR & LIBRARY",
    icon: "📷", color: "#6C5CE7",
    desc: "Exposure, tone, HSL, split toning, presets, and batch export.",
    features: ["Full develop engine (exposure, contrast, HSL...)", "Tone curve editor", "Split toning", "10 built-in presets", "Star ratings & color labels", "Before/after & batch export"],
    tier: "PRO · 400 lines",
  },
];

export default function Launcher({ onLaunch }) {
  const [hoveredApp, setHoveredApp] = useState(null);

  return (
    <div style={{
      width: "100vw", height: "100vh", background: T.bg,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      fontFamily: '"Source Sans 3", sans-serif', overflow: "hidden",
    }}>
      {/* Background grid */}
      <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: `linear-gradient(${T.gold} 1px, transparent 1px), linear-gradient(90deg, ${T.gold} 1px, transparent 1px)`, backgroundSize: "60px 60px" }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "48px", position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: "48px", fontFamily: '"Cormorant Garamond", serif', fontWeight: 700, color: T.gold, letterSpacing: "8px" }}>
          RESONANCE
        </div>
        <div style={{ fontSize: "14px", color: T.textMuted, letterSpacing: "12px", marginTop: "4px" }}>
          CREATIVE SUITE
        </div>
        <div style={{ width: "80px", height: "1px", background: `linear-gradient(90deg, transparent, ${T.gold}, transparent)`, margin: "20px auto 0" }} />
      </div>

      {/* App Grid */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "16px",
        maxWidth: "1100px", width: "90%", position: "relative", zIndex: 1,
      }}>
        {APPS.map(app => (
          <div
            key={app.id}
            onClick={() => onLaunch(app.id)}
            onMouseEnter={() => setHoveredApp(app.id)}
            onMouseLeave={() => setHoveredApp(null)}
            style={{
              background: hoveredApp === app.id ? `${app.color}08` : T.surface,
              border: `1px solid ${hoveredApp === app.id ? app.color + "44" : T.border}`,
              borderRadius: "8px", padding: "24px 16px", cursor: "pointer",
              transition: "all 0.3s ease",
              transform: hoveredApp === app.id ? "translateY(-4px)" : "none",
              boxShadow: hoveredApp === app.id ? `0 8px 30px ${app.color}15` : "none",
            }}
          >
            <div style={{ fontSize: "36px", textAlign: "center", marginBottom: "12px" }}>{app.icon}</div>
            <div style={{
              fontSize: "16px", fontFamily: '"Cormorant Garamond", serif', fontWeight: 700,
              color: app.color, textAlign: "center", letterSpacing: "2px",
            }}>{app.name}</div>
            <div style={{ fontSize: "6px", color: T.textMuted, textAlign: "center", letterSpacing: "2px", marginTop: "2px" }}>
              {app.subtitle}
            </div>
            <div style={{ width: "30px", height: "1px", background: app.color + "44", margin: "10px auto" }} />
            <div style={{ fontSize: "9px", color: T.textDim, textAlign: "center", lineHeight: 1.5 }}>
              {app.desc}
            </div>
            {hoveredApp === app.id && (
              <div style={{ marginTop: "12px" }}>
                {app.features.map((f, i) => (
                  <div key={i} style={{ fontSize: "7px", color: T.textMuted, padding: "1px 0", display: "flex", gap: "4px" }}>
                    <span style={{ color: app.color }}>·</span> {f}
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: "7px", color: T.goldDim, textAlign: "center", marginTop: "10px", letterSpacing: "1px" }}>
              {app.tier}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ marginTop: "48px", textAlign: "center", position: "relative", zIndex: 1 }}>
        <button
          data-launch="settings"
          onClick={() => onLaunch("settings")}
          style={{
            background: "transparent", color: T.textDim, border: `1px solid ${T.border}`, borderRadius: "3px",
            padding: "4px 12px", fontSize: "10px", letterSpacing: "2px", cursor: "pointer", marginBottom: "14px",
            fontFamily: '"Source Sans 3", sans-serif',
          }}
        >
          SETTINGS
        </button>
        <div style={{ fontSize: "10px", color: T.textMuted }}>
          Main Street Group LLC · 3,668 lines of pure React · Zero dependencies
        </div>
        <div style={{
          fontSize: "12px", fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic",
          color: T.goldDim, marginTop: "8px",
        }}>
          Every Creation Tells a Story
        </div>
      </div>

      {/* Font import */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Source+Sans+3:wght@300;400;600;700&display=swap');`}</style>
    </div>
  );
}
